import { useEffect, useMemo, useState } from 'react'
import { Link, useOutletContext, useParams } from 'react-router-dom'
import {
  AlertCircle,
  ClipboardList,
  ChevronRight,
  User,
  MousePointerClick,
  Layers,
  Hash,
  Spline,
  ScanEye,
  Stethoscope,
  CalendarDays,
} from 'lucide-react'
import { apiGet } from '../../lib/api.js'
import { nameInitial } from '../../lib/text.js'
import {
  asList,
  parseDateObj,
  formatThaiDate,
  buildChartIndex,
  getDoctorImage,
  chipStyles,
  tagTypeMeta,
  normalizeHxTag,
} from '../../lib/chart-helpers.js'
import { fetchAllTreatments } from '../../lib/billing.js'
import { usePatientLive } from '../../lib/patientLive.js'
import { sanitizeHtml, toHtml, isHtmlEmpty } from '../../lib/html-utils.js'
import { classifyTooth } from '../../components/ToothChart/data.js'
import { Odontogram, ADULT_QUADRANTS, PEDO_QUADRANTS } from '../../components/ToothChart/Odontogram.jsx'
import OralExamChart from '../../components/OralExamChart.jsx'

const THAI_MONTHS_SHORT = [
  'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
  'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.',
]

export default function PatientChartSummary() {
  const { id } = useParams()
  const { live } = useOutletContext() || {}
  // Bumped by a realtime event → refetches in place.
  const [tick, setTick] = useState(0)
  const [treatments, setTreatments] = useState(null)
  const [charts, setCharts]         = useState(null)
  const [error, setError]           = useState(null)

  const [view, setView]             = useState('clinical')   // 'clinical' | 'oral'
  const [decided, setDecided]       = useState(false)
  const [clinicalTooth, setClinicalTooth] = useState(null)
  const [snapIndex, setSnapIndex]   = useState(0)

  // Live: the doctor portal writing Hx or an oral exam, and new visits. The
  // odontogram is derived from `charts`, so it redraws itself.
  usePatientLive(live, ['chart', 'treatment'], () => setTick((t) => t + 1))

  // Reset the view state only when the PATIENT changes — a live refetch must not
  // throw the reader back to the default tooth/snapshot selection.
  useEffect(() => {
    setTreatments(null); setCharts(null); setError(null)
    setDecided(false); setClinicalTooth(null); setSnapIndex(0)
  }, [id])

  useEffect(() => {
    let alive = true
    Promise.allSettled([
      fetchAllTreatments(id),
      apiGet('/api/charts', { patient_id: id }),
    ]).then(([tRes, cRes]) => {
      if (!alive) return
      if (tRes.status === 'fulfilled') setTreatments(tRes.value)
      else { setError(tRes.reason?.message || 'โหลด treatment ไม่สำเร็จ'); setTreatments([]) }
      if (cRes.status === 'fulfilled') setCharts(asList(cRes.value))
      else setCharts([])
    })
    return () => { alive = false }
  }, [id, tick])

  const chartIndex = useMemo(() => buildChartIndex(charts || []), [charts])

  /* ---- Clinical (hx) aggregation, by tooth + split non-tooth buckets ---- */
  const clinical = useMemo(() => {
    const byTooth = new Map()
    const groups = { fullMouth: [], ortho: [], untagged: [] }
    for (const t of treatments || []) {
      const chart = chartIndex.findFor(t)
      if (!chart || !Array.isArray(chart.hx)) continue
      chart.hx.forEach((hx, hxIndex) => {
        // An entry with no tag at all is old history from before Tooth Tagging. normalizeHxTag reads that as
        // Full Mouth (ClinicalEditor relies on it); here it belongs in ไม่ได้ระบุซี่ฟัน instead.
        const tagged = Boolean(hx?.tag_type || hx?.tagType) || (Array.isArray(hx?.tag) && hx.tag.length > 0)
        const { tag_type, teeth } = tagged ? normalizeHxTag(hx) : { tag_type: '', teeth: [] }
        const rec = { treatment: t, chart, hx, hxIndex, tag_type }
        if (teeth.length > 0) {
          for (const tooth of teeth) {
            if (!byTooth.has(tooth)) byTooth.set(tooth, [])
            byTooth.get(tooth).push(rec)
          }
        } else if (tag_type === 'Ortho') {
          groups.ortho.push(rec)
        } else if (tag_type === 'Full Mouth') {
          groups.fullMouth.push(rec)
        } else {
          groups.untagged.push(rec)   // no tag (old history), or Adult/Ped with no specific teeth
        }
      })
    }
    for (const arr of byTooth.values()) arr.sort(byDateDesc)
    groups.fullMouth.sort(byDateDesc); groups.ortho.sort(byDateDesc); groups.untagged.sort(byDateDesc)
    const hasPedo = [...byTooth.keys()].some((n) => classifyTooth(n) === 'pedo')
    let records = groups.fullMouth.length + groups.ortho.length + groups.untagged.length
    for (const arr of byTooth.values()) records += arr.length
    return { byTooth, groups, hasPedo, teethTouched: byTooth.size, records }
  }, [treatments, chartIndex])

  /* ---- Oral-exam snapshots (one per visit that has oral_exam[] data) ---- */
  const oralSnapshots = useMemo(() => {
    const snaps = []
    for (const t of treatments || []) {
      const chart = chartIndex.findFor(t)
      const raw = Array.isArray(chart?.oral_exam) ? chart.oral_exam : []
      if (raw.length === 0) continue
      const findings = raw.map((e) => ({
        tag: Array.isArray(e?.tag) ? String(e.tag[0] || '') : String(e?.tag || ''),
        type: e?.type || '',
        detail: e?.detail || '',
      }))
      snaps.push({
        treatmentId: t.id ?? t.treatment_id,
        date: t.date,
        treatment: t,
        chart,
        findings,
        findingCount: findings.length,
      })
    }
    snaps.sort((a, b) => byDateDesc({ treatment: a.treatment }, { treatment: b.treatment }))
    return snaps
  }, [treatments, chartIndex])

  // Pick a sensible default view once data lands: prefer การรักษา when present.
  useEffect(() => {
    if (decided || treatments === null || charts === null) return
    setView(clinical.records > 0 ? 'clinical' : (oralSnapshots.length > 0 ? 'oral' : 'clinical'))
    setDecided(true)
  }, [decided, treatments, charts, oralSnapshots.length, clinical.records])

  // Keep snapIndex in range when the snapshot list changes.
  useEffect(() => { if (snapIndex > oralSnapshots.length - 1) setSnapIndex(0) }, [oralSnapshots.length, snapIndex])

  const loading = treatments === null || charts === null

  return (
    <div>
      <div className="mb-5">
        <p className="text-[14px] text-slate-500">
          สรุปสภาพช่องปากตามฟันแต่ละซี่ — สลับดูได้ระหว่าง <b className="font-semibold text-slate-600">การรักษา</b> (รวมทุก visit) และ <b className="font-semibold text-slate-600">Oral Exam</b> (สถานะฟันตามวันที่ตรวจ)
        </p>
        {!loading && !error && <ViewToggle view={view} setView={setView} />}
      </div>

      {loading && <SkeletonState />}
      {!loading && error && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-5 text-rose-700 text-[14px] flex items-start gap-2">
          <AlertCircle size={16} className="flex-none mt-0.5" />
          <div>โหลดข้อมูลไม่สำเร็จ: {error}</div>
        </div>
      )}

      {!loading && !error && view === 'clinical' && (
        <ClinicalView
          patientId={id}
          clinical={clinical}
          selectedTooth={clinicalTooth}
          setSelectedTooth={setClinicalTooth}
        />
      )}

      {!loading && !error && view === 'oral' && (
        <OralExamView
          patientId={id}
          snapshots={oralSnapshots}
          snapIndex={snapIndex}
          setSnapIndex={setSnapIndex}
        />
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* View toggle                                                                */
/* -------------------------------------------------------------------------- */

// Same pill row as the History detail's section submenu, so the two Chart sub-pages read alike.
function ViewToggle({ view, setView }) {
  return (
    <div className="mt-3 pb-2 border-b border-slate-200 flex items-center gap-1 overflow-x-auto no-scrollbar">
      {[
        { key: 'clinical', label: 'การรักษา',   icon: Stethoscope },
        { key: 'oral',     label: 'Oral Exam', icon: ScanEye },
      ].map(({ key, label, icon: Icon }) => {
        const active = view === key
        return (
          <button
            key={key}
            type="button"
            onClick={() => setView(key)}
            className={`inline-flex items-center gap-1.5 whitespace-nowrap px-3 py-1.5 rounded-full text-[14px] font-medium transition-all ${
              active ? 'bg-brand-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
            }`}
          >
            <Icon size={12} strokeWidth={2} />
            {label}
          </button>
        )
      })}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Oral Exam view (per-visit, with date selector)                             */
/* -------------------------------------------------------------------------- */

function OralExamView({ patientId, snapshots, snapIndex, setSnapIndex }) {
  // group snapshots by calendar day (snapshots are already sorted newest-first)
  const dayGroups = useMemo(() => {
    const map = new Map()
    snapshots.forEach((s, i) => {
      const d = parseDateObj(s.date)
      const key = d ? `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}` : String(s.date)
      if (!map.has(key)) map.set(key, { date: s.date, idxs: [] })
      map.get(key).idxs.push(i)
    })
    return [...map.values()]
  }, [snapshots])

  if (snapshots.length === 0) {
    return <EmptyState title="ยังไม่มีบันทึก Oral Exam" hint="เมื่อมีการบันทึกสถานะฟันในแต่ละ visit จะสรุปแสดงที่นี่" />
  }
  const idx = Math.min(snapIndex, snapshots.length - 1)
  const snap = snapshots[idx]
  const activeDay = dayGroups.find((g) => g.idxs.includes(idx)) || dayGroups[0]

  const al     = snap.treatment.appointmentlist || snap.treatment.appointmentList || {}
  const alType = al.type || snap.treatment.type || ''
  const alColor = al.color
  const doctor = snap.treatment.doctor || {}
  const docName = (doctor.code || doctor.name || doctor.full_name || '').trim()

  // short label distinguishing one exam from another within the same day
  const examLabel = (s) => {
    const a = s.treatment.appointmentlist || s.treatment.appointmentList || {}
    const ty = (a.type || s.treatment.type || '').trim()
    const dr = s.treatment.doctor || {}
    const dn = (dr.code || dr.name || dr.full_name || '').trim()
    return ty || dn || 'ตรวจ'
  }

  return (
    <div className="space-y-5">
      {/* Day selector — one pill per day (same-day visits collapse into one) */}
      <div>
        <div className="flex items-center gap-1.5 mb-2 text-[12.5px] uppercase tracking-[0.12em] font-semibold text-slate-400">
          <CalendarDays size={12} strokeWidth={2} /> เลือกวันที่ตรวจ
        </div>
        <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar pb-1">
          {dayGroups.map((g, di) => (
            <DatePill
              key={g.date + '-' + di}
              date={g.date}
              count={g.idxs.reduce((n, j) => n + snapshots[j].findingCount, 0)}
              exams={g.idxs.length}
              latest={di === 0}
              active={g === activeDay}
              onClick={() => setSnapIndex(g.idxs[0])}
            />
          ))}
        </div>
      </div>

      {/* Same-day exam selector — only when the active day has more than one exam */}
      {activeDay.idxs.length > 1 && (
        <div>
          <div className="mb-1.5 text-[12px] text-slate-400">
            มี {activeDay.idxs.length} รายการตรวจในวันนี้ — เลือกรายการที่จะแสดง
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {activeDay.idxs.map((j, k) => (
              <button
                key={snapshots[j].treatmentId}
                type="button"
                onClick={() => setSnapIndex(j)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[13px] font-medium ring-1 transition ${
                  j === idx ? 'bg-brand-50 text-brand-700 ring-brand-300' : 'bg-white text-slate-600 ring-slate-200 hover:ring-slate-300'
                }`}
              >
                <span className="text-slate-400">#{k + 1}</span>
                {examLabel(snapshots[j])}
                <span className={j === idx ? 'text-brand-400' : 'text-slate-400'}>· {snapshots[j].findingCount}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Selected visit context */}
      <div className="flex items-center gap-3 flex-wrap text-[14px]">
        <span className="font-semibold text-slate-800">{formatThaiDate(snap.date)}</span>
        {alType && (
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[13.5px] font-semibold" style={chipStyles(alColor)}>
            <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />{alType}
          </span>
        )}
        {docName && <span className="text-slate-500">· {docName}</span>}
        <Link
          to={`/patient/${patientId}/chart/history/${snap.treatmentId}`}
          state={{ treatment: snap.treatment, chart: snap.chart }}
          className="inline-flex items-center gap-1 text-brand-600 hover:text-brand-700 font-medium ml-auto"
        >
          เปิด visit นี้ <ChevronRight size={13} />
        </Link>
      </div>

      {/* Marked odontogram — keyed by visit so tooth selection resets per snapshot */}
      <OralExamChart key={snap.treatmentId} findings={snap.findings} />
    </div>
  )
}

function DatePill({ date, count, exams = 1, latest, active, onClick }) {
  const d = parseDateObj(date)
  const label = d ? `${d.getDate()} ${THAI_MONTHS_SHORT[d.getMonth()]} ${String(d.getFullYear() % 100).padStart(2, '0')}` : '—'
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-none inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[13.5px] font-medium ring-1 transition-all ${
        active
          ? 'bg-brand-600 text-white ring-brand-600 shadow-sm'
          : 'bg-white text-slate-600 ring-slate-200 hover:ring-slate-300 hover:text-slate-900'
      }`}
    >
      <span className="tabular-nums">{label}</span>
      {latest && (
        <span className={`text-[11px] px-1 rounded ${active ? 'bg-white/25 text-white' : 'bg-emerald-50 text-emerald-600'}`}>ล่าสุด</span>
      )}
      {exams > 1 && (
        <span className={`text-[11px] px-1 rounded ${active ? 'bg-white/25 text-white' : 'bg-violet-50 text-violet-600'}`}>{exams} ครั้ง</span>
      )}
      <span className={`text-[11.5px] tabular-nums ${active ? 'text-white/80' : 'text-slate-400'}`}>{count}</span>
    </button>
  )
}

/* -------------------------------------------------------------------------- */
/* Clinical (treatment) view                                                  */
/* -------------------------------------------------------------------------- */

function ClinicalView({ patientId, clinical, selectedTooth, setSelectedTooth }) {
  if (clinical.records === 0) {
    return <EmptyState title="ยังไม่มีบันทึกการรักษาตามซี่ฟัน" hint="เมื่อมีการบันทึก oral examination ในแต่ละ visit จะสรุปแสดงที่นี่" />
  }
  const selectedRecords = selectedTooth ? (clinical.byTooth.get(selectedTooth) || []) : null
  const makeCell = (tone) => (num) => (
    <CountToothCell
      key={num}
      num={num}
      count={clinical.byTooth.get(num)?.length || 0}
      selected={selectedTooth === num}
      onSelect={(n) => setSelectedTooth((cur) => (cur === n ? null : n))}
      tone={tone}
    />
  )

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-4 text-[13.5px] text-slate-500">
        <Stat dotClass="bg-brand-500" label="ฟันที่มีบันทึก" value={clinical.teethTouched} />
        <Stat dotClass="bg-emerald-500" label="บันทึกทั้งหมด" value={clinical.records} />
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-5 overflow-x-auto">
        <Odontogram quadrants={ADULT_QUADRANTS} label="ฟันแท้ (Adult)" renderCell={makeCell('blue')} />
        {clinical.hasPedo && (
          <div className="mt-6 pt-5 border-t border-dashed border-slate-200">
            <Odontogram quadrants={PEDO_QUADRANTS} label="ฟันน้ำนม (Pedo)" renderCell={makeCell('violet')} />
          </div>
        )}
      </div>

      {selectedTooth ? (
        <ToothRecordPanel patientId={patientId} tooth={selectedTooth} records={selectedRecords} onClose={() => setSelectedTooth(null)} />
      ) : (
        <HintBox icon={MousePointerClick} text="คลิกฟันที่มีบันทึกด้านบน เพื่อดูรายการการรักษาของฟันซี่นั้น" />
      )}

      {/* Non-tooth-specific records — sub-tabs by category */}
      <NonToothTabs patientId={patientId} groups={clinical.groups} />
    </div>
  )
}

function NonToothTabs({ patientId, groups }) {
  const cats = [
    { key: 'fullMouth', label: 'Full Mouth · ทั้งปาก', icon: Layers, iconClass: 'text-amber-500',  records: groups.fullMouth },
    { key: 'ortho',     label: 'Ortho · จัดฟัน',        icon: Spline, iconClass: 'text-pink-500',   records: groups.ortho },
    { key: 'untagged',  label: 'ไม่ได้ระบุซี่ฟัน',        icon: Hash,   iconClass: 'text-slate-400',  records: groups.untagged },
  ].filter((c) => c.records.length > 0)

  const [active, setActive] = useState(cats[0]?.key || null)
  if (cats.length === 0) return null
  const current = cats.find((c) => c.key === active) || cats[0]

  return (
    <section>
      <div className="flex items-center gap-1.5 mb-3 flex-wrap">
        {cats.map((c) => {
          const on = c.key === current.key
          const Icon = c.icon
          return (
            <button
              key={c.key}
              type="button"
              onClick={() => setActive(c.key)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[14px] font-medium ring-1 transition-all ${
                on ? 'bg-brand-600 text-white ring-brand-600 shadow-sm' : 'bg-white text-slate-600 ring-slate-200 hover:ring-slate-300 hover:text-slate-900'
              }`}
            >
              <Icon size={13} strokeWidth={2} className={on ? '' : c.iconClass} />
              {c.label}
              <span className={`text-[11.5px] tabular-nums ${on ? 'text-white/80' : 'text-slate-400'}`}>{c.records.length}</span>
            </button>
          )
        })}
      </div>
      {current.key === 'untagged' && (
        <div className="mb-2 text-[13px] text-slate-400">
          บันทึกที่ไม่มี Tooth Tag — ส่วนใหญ่เป็นประวัติเก่าก่อนมีระบบ Tooth Tag จึงไม่ขึ้นบนผังฟัน
        </div>
      )}
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        {current.records.map((rec, i) => (
          <RecordRow
            key={`${rec.treatment.id ?? rec.treatment.treatment_id}-${rec.hxIndex}`}
            patientId={patientId}
            rec={rec}
            isLast={i === current.records.length - 1}
          />
        ))}
      </div>
    </section>
  )
}

function ToothRecordPanel({ patientId, tooth, records, onClose }) {
  const kind = classifyTooth(tooth) === 'pedo' ? 'ฟันน้ำนม' : 'ฟันแท้'
  return (
    <section className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
      <header className="px-5 pt-4 pb-3.5 flex items-center gap-3 border-b border-slate-100">
        <div className="w-11 h-11 rounded-xl bg-brand-50 text-brand-700 flex items-center justify-center flex-none ring-1 ring-brand-100 font-mono text-[16px] font-semibold">{tooth}</div>
        <div className="flex-1 min-w-0">
          <div className="text-[16px] font-semibold text-slate-900 tracking-tight">ฟันซี่ {tooth}</div>
          <div className="text-[13.5px] text-slate-400 -mt-0.5">{kind} · {records.length} บันทึก</div>
        </div>
        <button type="button" onClick={onClose} className="text-[13.5px] text-slate-500 hover:text-brand-700 px-2.5 py-1.5 rounded-lg hover:bg-slate-50 transition-colors">ล้างที่เลือก</button>
      </header>
      <div className="divide-y divide-slate-100">
        {records.map((rec, i) => (
          <RecordRow
            key={`${rec.treatment.id ?? rec.treatment.treatment_id}-${rec.hxIndex}`}
            patientId={patientId}
            rec={rec}
            highlightTooth={tooth}
            isLast={i === records.length - 1}
          />
        ))}
      </div>
    </section>
  )
}

function RecordRow({ patientId, rec, highlightTooth, isLast }) {
  const { treatment: t, chart, hx, tag_type } = rec
  const tid = t.id ?? t.treatment_id
  const d = parseDateObj(t.date)
  const day   = d ? String(d.getDate()).padStart(2, '0') : '--'
  const month = d ? THAI_MONTHS_SHORT[d.getMonth()] : ''
  const year  = d ? String(d.getFullYear() % 100).padStart(2, '0') : ''

  const al     = t.appointmentlist || t.appointmentList || {}
  const alType = al.type || t.type || '—'
  const alColor = al.color
  const tt = tagTypeMeta(tag_type)

  const { teeth } = normalizeHxTag(hx)
  const PE   = toHtml(hx.PE || '')
  const note = hx.note || ''
  const hasPE = !isHtmlEmpty(PE)

  const doctor = t.doctor || {}
  const docName = (doctor.code || doctor.name || doctor.full_name || '').trim()
  const docImg  = getDoctorImage(t)

  return (
    <Link
      to={`/patient/${patientId}/chart/history/${tid}`}
      state={{ treatment: t, chart }}
      className={`group flex items-start gap-4 px-5 py-4 hover:bg-slate-50/70 transition-colors ${isLast ? '' : 'border-b border-slate-100'}`}
    >
      <div className="flex-none w-11 text-center pt-0.5">
        <div className="text-[19px] font-semibold text-slate-900 leading-none tabular-nums">{day}</div>
        <div className="text-[12px] text-slate-400 font-medium mt-1">{month} {year}</div>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[13.5px] font-semibold" style={chipStyles(alColor)}>
            <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />{alType}
          </span>
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[12.5px] font-semibold ring-1 ${tt.bg} ${tt.text} ${tt.ring}`}>{tag_type ? tt.label : 'ไม่มี Tooth Tag'}</span>
        </div>

        {teeth.length > 0 && (
          <div className="flex items-center gap-1 flex-wrap mt-2">
            {teeth.map((tooth, ti) => (
              <span key={ti} className={`inline-flex items-center justify-center min-w-[26px] px-1.5 h-[22px] rounded-md text-[12.5px] font-mono ring-1 ${
                tooth === highlightTooth ? 'bg-brand-600 text-white ring-brand-600' : 'bg-white text-slate-600 ring-slate-200'
              }`}>{tooth}</span>
            ))}
          </div>
        )}

        {hasPE && (
          <div className="text-[14px] text-slate-700 mt-2 line-clamp-3 [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-5 [&_ol]:pl-5 [&_b]:font-semibold [&_strong]:font-semibold" dangerouslySetInnerHTML={{ __html: sanitizeHtml(PE) }} />
        )}
        {note && <div className="text-[14px] text-slate-500 italic mt-1.5 line-clamp-2 whitespace-pre-line">{note}</div>}
        {!hasPE && !note && <div className="text-[14px] text-slate-400 italic mt-1.5">ไม่มีรายละเอียดบันทึก</div>}

        {docName && (
          <div className="flex items-center gap-1.5 mt-2 text-[13.5px] text-slate-500">
            <DoctorAvatar src={docImg} name={docName} /><span className="truncate">{docName}</span>
          </div>
        )}
      </div>

      <ChevronRight size={14} className="flex-none mt-1 text-slate-300 group-hover:text-brand-500 group-hover:translate-x-0.5 transition-all" />
    </Link>
  )
}

/* -------------------------------------------------------------------------- */
/* Atoms                                                                      */
/* -------------------------------------------------------------------------- */

const COUNT_TONE = {
  blue: {
    has:      'bg-brand-50 ring-brand-300 text-brand-700 hover:ring-brand-400',
    selected: 'bg-brand-600 ring-brand-600 text-white shadow-sm',
    badge:    'bg-brand-600 text-white',
    badgeSel: 'bg-white text-brand-700',
  },
  violet: {
    has:      'bg-violet-50 ring-violet-300 text-violet-700 hover:ring-violet-400',
    selected: 'bg-violet-600 ring-violet-600 text-white shadow-sm',
    badge:    'bg-violet-600 text-white',
    badgeSel: 'bg-white text-violet-700',
  },
}

function CountToothCell({ num, count, selected, onSelect, tone }) {
  const T = COUNT_TONE[tone] || COUNT_TONE.blue
  const has = count > 0
  return (
    <button
      type="button"
      disabled={!has}
      onClick={() => has && onSelect(num)}
      title={has ? `ฟัน ${num} · ${count} บันทึก` : `ฟัน ${num} · ไม่มีบันทึก`}
      aria-pressed={selected}
      className={`relative flex items-center justify-center w-[32px] h-[38px] rounded-md ring-1 text-[12.5px] font-mono transition-all select-none ${
        has ? 'cursor-pointer hover:-translate-y-[1px] z-10' : 'cursor-default'
      } ${selected ? T.selected : has ? T.has : 'bg-white ring-slate-200 text-slate-300'}`}
    >
      {num}
      {has && (
        <span className={`absolute -top-1.5 -right-1.5 z-10 min-w-[15px] h-[15px] px-1 rounded-full text-[10.5px] font-semibold leading-[15px] text-center ${selected ? T.badgeSel : T.badge}`}>{count}</span>
      )}
    </button>
  )
}

function HintBox({ icon: Icon, text }) {
  return (
    <div className="rounded-2xl border border-dashed border-slate-200 bg-white/60 p-8 flex flex-col items-center justify-center text-center">
      <div className="w-10 h-10 rounded-xl bg-brand-50 text-brand-600 flex items-center justify-center mb-3">
        <Icon size={18} strokeWidth={1.75} />
      </div>
      <div className="text-[14px] text-slate-500">{text}</div>
    </div>
  )
}

function Stat({ dotClass, label, value }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`w-1.5 h-1.5 rounded-full ${dotClass}`} />
      <span>{label}</span>
      <span className="font-semibold text-slate-700 tabular-nums">{value}</span>
    </span>
  )
}

function DoctorAvatar({ src, name }) {
  const initial = nameInitial(name)
  return (
    <div className="w-5 h-5 rounded-full flex items-center justify-center flex-none ring-1 ring-slate-200 overflow-hidden bg-gradient-to-br from-slate-100 to-slate-50">
      {src
        ? <img src={src} alt={name || ''} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
        : (initial !== '?' ? <span className="text-[11.5px] font-semibold text-slate-500">{initial}</span> : <User size={11} className="text-slate-400" strokeWidth={1.75} />)}
    </div>
  )
}

function SkeletonState() {
  return (
    <div className="space-y-5">
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="h-9 bg-slate-100 rounded animate-pulse w-full max-w-2xl" />
        <div className="h-9 bg-slate-100 rounded animate-pulse w-full max-w-2xl mt-3" />
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100 overflow-hidden">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex items-start gap-4 px-5 py-4">
            <div className="w-11 h-9 bg-slate-100 rounded animate-pulse" />
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-slate-100 rounded animate-pulse w-1/3" />
              <div className="h-3 bg-slate-100 rounded animate-pulse w-2/3" />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function EmptyState({ title, hint }) {
  return (
    <div className="rounded-2xl border border-dashed border-slate-200 bg-white/60 p-14 text-center">
      <div className="w-11 h-11 rounded-xl bg-slate-100 text-slate-400 flex items-center justify-center mx-auto mb-3">
        <ClipboardList size={20} strokeWidth={1.75} />
      </div>
      <div className="text-[14.5px] font-medium text-slate-700">{title}</div>
      <div className="text-[13.5px] text-slate-400 mt-1">{hint}</div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function byDateDesc(a, b) {
  return (parseDateObj(b.treatment.date)?.getTime() ?? 0) - (parseDateObj(a.treatment.date)?.getTime() ?? 0)
}

